import type { CreateWorkfrontConfig } from '@wf-coe/types';
import {
  Button,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
} from 'flowbite-react';
import { type FormEvent, useEffect, useState } from 'react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import { SecretsRowButton } from '../../components/clients/SecretsRowButton.js';

const WORKFRONT_CONFIG_BUNDLE_FIELDS = [
  { key: 'apiKey', label: 'Workfront API key', kind: 'string' as const },
];
import { useParams } from 'react-router-dom';
import { useCreateWorkfrontConfigMutation } from '../../queries/use-create-workfront-config.js';
import { useDeleteWorkfrontConfigMutation } from '../../queries/use-delete-workfront-config.js';
import { type OAuthAppDto, useOAuthAppsQuery } from '../../queries/use-oauth-apps.js';
import { useUpdateWorkfrontConfigMutation } from '../../queries/use-update-workfront-config.js';
import {
  type WorkfrontConfigDto,
  useWorkfrontConfigsQuery,
} from '../../queries/use-workfront-configs.js';

/**
 * `/clients/{id}/workfront-connections` — manages a tenant's
 * Workfront API endpoints + the OAuth apps that authenticate against
 * them. JWT/Web OAuth-app pickers are populated from the same
 * `useOAuthAppsQuery` the OAuth Apps page uses.
 */
export function WorkfrontConnections(): JSX.Element {
  const { id: clientId } = useParams<{ id: string }>();
  const { data, error, isLoading, isError } = useWorkfrontConfigsQuery(clientId);
  const oauthAppsQuery = useOAuthAppsQuery(clientId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<WorkfrontConfigDto | null>(null);
  const [deleting, setDeleting] = useState<WorkfrontConfigDto | null>(null);

  const oauthApps = oauthAppsQuery.data ?? [];

  const handleNew = (): void => {
    setEditing(null);
    setEditorOpen(true);
  };
  const handleEdit = (config: WorkfrontConfigDto): void => {
    setEditing(config);
    setEditorOpen(true);
  };

  const oauthAppNameById = new Map(oauthApps.map((a) => [a.id, a.name]));

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          Workfront Connections
        </h2>
        <Button size="sm" onClick={handleNew} disabled={!clientId}>
          <FiPlus className="mr-1" /> New Workfront connection
        </Button>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
          Loading Workfront connections…
        </p>
      ) : isError ? (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
        >
          {error instanceof Error ? error.message : 'Failed to load Workfront connections'}
        </div>
      ) : data && data.length === 0 ? (
        <EmptyState onCreate={handleNew} />
      ) : data ? (
        <ConfigsTable
          configs={data}
          clientId={clientId}
          oauthAppNameById={oauthAppNameById}
          onEdit={handleEdit}
          onDelete={setDeleting}
        />
      ) : null}

      {clientId ? (
        <ConfigEditModal
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          clientId={clientId}
          existing={editing}
          oauthApps={oauthApps}
        />
      ) : null}
      {clientId && deleting ? (
        <ConfigDeleteModal
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          clientId={clientId}
          config={deleting}
        />
      ) : null}
    </section>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center dark:border-gray-600 dark:bg-gray-800">
      <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
        No Workfront connections yet
      </h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
        Add a Workfront URL + OAuth app pairing so the audit + backup engines know where to talk.
      </p>
      <Button size="sm" className="mx-auto mt-4" onClick={onCreate}>
        <FiPlus className="mr-1" /> New Workfront connection
      </Button>
    </div>
  );
}

function ConfigsTable({
  configs,
  clientId,
  oauthAppNameById,
  onEdit,
  onDelete,
}: {
  configs: WorkfrontConfigDto[];
  clientId: string | undefined;
  oauthAppNameById: Map<string, string>;
  onEdit: (config: WorkfrontConfigDto) => void;
  onDelete: (config: WorkfrontConfigDto) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>URL</TableHeadCell>
            <TableHeadCell>JWT OAuth App</TableHeadCell>
            <TableHeadCell>Web OAuth App</TableHeadCell>
            <TableHeadCell>
              <span className="sr-only">Actions</span>
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
          {configs.map((config) => (
            <TableRow key={config.id} className="bg-white dark:bg-gray-800">
              <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                {config.name}
              </TableCell>
              <TableCell className="font-mono text-xs">{config.url}</TableCell>
              <TableCell>
                {config.jwtOauthAppId ? (oauthAppNameById.get(config.jwtOauthAppId) ?? '—') : '—'}
              </TableCell>
              <TableCell>
                {config.webOauthAppId ? (oauthAppNameById.get(config.webOauthAppId) ?? '—') : '—'}
              </TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  {clientId ? (
                    <SecretsRowButton
                      bundlePath={`/v1/clients/${clientId}/workfront-configs/${config.id}`}
                      fields={WORKFRONT_CONFIG_BUNDLE_FIELDS}
                      title={`${config.name} — secrets`}
                      ariaLabel={`Manage secrets for ${config.name}`}
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => onEdit(config)}
                    aria-label={`Edit ${config.name}`}
                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(config)}
                    aria-label={`Delete ${config.name}`}
                    className="rounded p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-950 dark:hover:text-red-300"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ConfigEditModal({
  open,
  onClose,
  clientId,
  existing,
  oauthApps,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  existing: WorkfrontConfigDto | null;
  oauthApps: OAuthAppDto[];
}): JSX.Element {
  const create = useCreateWorkfrontConfigMutation();
  const update = useUpdateWorkfrontConfigMutation();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [jwtOauthAppId, setJwtOauthAppId] = useState('');
  const [webOauthAppId, setWebOauthAppId] = useState('');

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setUrl(existing?.url ?? '');
      setJwtOauthAppId(existing?.jwtOauthAppId ?? '');
      setWebOauthAppId(existing?.webOauthAppId ?? '');
      create.reset();
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const canSubmit = name.trim() !== '' && url.trim() !== '' && !isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const body: CreateWorkfrontConfig = {
      name: name.trim(),
      url: url.trim(),
      jwtOauthAppId: jwtOauthAppId === '' ? null : jwtOauthAppId,
      webOauthAppId: webOauthAppId === '' ? null : webOauthAppId,
    };
    if (existing) {
      update.mutate({ clientId, configId: existing.id, patch: body }, { onSuccess: onClose });
    } else {
      create.mutate({ clientId, body }, { onSuccess: onClose });
    }
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="lg">
      <ModalHeader>
        {existing ? 'Edit Workfront connection' : 'New Workfront connection'}
      </ModalHeader>
      <form onSubmit={handleSubmit} aria-label="Workfront connection form">
        <ModalBody>
          <div className="space-y-4">
            <div>
              <Label htmlFor="wf-name">
                Name <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="wf-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isPending}
              />
            </div>
            <div>
              <Label htmlFor="wf-url">
                URL <span className="text-red-500">*</span>
              </Label>
              <TextInput
                id="wf-url"
                type="url"
                required
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://customer.my.workfront.com"
                disabled={isPending}
              />
              <HelperText className="mt-1">Tenant Workfront base URL (no trailing /).</HelperText>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="wf-jwt-oauth">JWT OAuth App</Label>
                <Select
                  id="wf-jwt-oauth"
                  value={jwtOauthAppId}
                  onChange={(e) => setJwtOauthAppId(e.target.value)}
                  disabled={isPending}
                >
                  <option value="">— None —</option>
                  {oauthApps.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="wf-web-oauth">Web OAuth App</Label>
                <Select
                  id="wf-web-oauth"
                  value={webOauthAppId}
                  onChange={(e) => setWebOauthAppId(e.target.value)}
                  disabled={isPending}
                >
                  <option value="">— None —</option>
                  {oauthApps.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            {oauthApps.length === 0 ? (
              <HelperText className="text-amber-700 dark:text-amber-400">
                No OAuth apps yet — create one on the OAuth Apps tab so you can link it here.
              </HelperText>
            ) : null}
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
              >
                {error instanceof Error ? error.message : 'Save failed'}
              </div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isPending ? 'Saving…' : existing ? 'Save' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function ConfigDeleteModal({
  open,
  onClose,
  clientId,
  config,
}: {
  open: boolean;
  onClose: () => void;
  clientId: string;
  config: WorkfrontConfigDto;
}): JSX.Element {
  const del = useDeleteWorkfrontConfigMutation();

  const handleConfirm = (): void => {
    del.mutate({ clientId, configId: config.id }, { onSuccess: onClose });
  };

  return (
    <Modal show={open} onClose={onClose} dismissible size="md">
      <ModalHeader>Delete Workfront connection</ModalHeader>
      <ModalBody>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          This will permanently delete <span className="font-semibold">{config.name}</span>. Any
          licenses pointing at it lose their reference (set to null) — the licenses themselves stay.
        </p>
        {del.isError ? (
          <div
            role="alert"
            className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            {del.error instanceof Error ? del.error.message : 'Failed to delete'}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={onClose} disabled={del.isPending}>
          Cancel
        </Button>
        <Button color="failure" onClick={handleConfirm} disabled={del.isPending}>
          {del.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
